import { FC, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Box, Tooltip, FormControlLabel, Switch } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/Restore";
import SaveIcon from "@mui/icons-material/Save";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import GenericErrorMessageModal from "@/components/forms/errorHandling/genericErrorMessageModal";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import useConfirmOnLeave from "@/helper/customHooks/useConfirmOnLeave";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import DraftBadge from "@/components/draftBadge/draftBadge";
import styles from "@/components/anchorDataOverview/anchorDataOverview.module.css";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
import AnchorDataOverviewAccordion from "@/components/anchorDataOverview/anchorDataOverviewAccordion";
import AnchorDataOverviewModal from "@/components/anchorDataOverview/anchorDataOverviewModal";
import AnchorDataOverviewCellInlineEditor from "@/components/anchorDataOverview/anchorDataOverviewCellInlineEditor";
import AnchorDataOverviewObjectListCellEditor from "@/components/anchorDataOverview/anchorDataOverviewObjectListCellEditor";
import {
  mapOverviewResultsToRows,
  fetchTemplateTypes,
  formatObjectListEntries,
  formatGermanDate,
  formatInheritedForDisplay,
  isSameAnchorDataValue,
  applyInlineEditsToOverviewRows,
} from "@/components/anchorDataOverview/anchorDataOverviewUtils";
import {
  clearAnchorDataOverviewTemplateIds,
  setAnchorDataOverviewTemplateIds,
} from "@/components/state/tableSearch/tableConfigSlice";
import { useRegisterUnsavedChanges } from "@/context/unsavedChangesContext";
import { getAnchorDataBulkCreateUpdateErrors } from "@/helper/anchorDataBulkCreateUpdate.utils";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { KAER_COMPANY_ID } from "@/helper/systemIds";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import {
  anchorDataOverviewEntityAdapter,
  getAnchorDataDateFilterOptions,
} from "@/components/anchorDataOverview/anchorDataOverviewEntityAdapter";

const ANCHOR_DATA_OVERVIEW_TABLE_CONFIG_ENTITY = "anchorDataOverviewTemplateSelection";
const EMPTY_TEMPLATE_IDS: string[] = [];
const INITIAL_SHOW_PARENT_COMPANIES_ONLY = true;
const INITIAL_SHOW_EMPTY_VALUES_ONLY = false;

const AnchorDataOverviewList: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("anchordata_overview"));
  const { showConfirmationModalAsync } = useConfirmationModal();
  const dispatch = useDispatch();
  const persistedTemplateIdsRaw = useSelector(
    (state: RootState) =>
      state.tableConfig?.[ANCHOR_DATA_OVERVIEW_TABLE_CONFIG_ENTITY]
        ?.anchorDataOverviewTemplateIds,
  );
  const persistedTemplateIds = useMemo(
    () =>
      Array.isArray(persistedTemplateIdsRaw)
        ? persistedTemplateIdsRaw
        : EMPTY_TEMPLATE_IDS,
    [persistedTemplateIdsRaw],
  );
  const isPersistedTemplateSelectionEmpty =
    !Array.isArray(persistedTemplateIdsRaw) || persistedTemplateIds.length === 0;

  const [selectedAnchorDataTemplates, setSelectedAnchorDataTemplates] =
    useState<(any | null)[]>([null]);
  const [lastSelectedTemplates, setLastSelectedTemplates] = useState<
    (any | null)[]
  >([null]);
  const [reorderCounter, setReorderCounter] = useState(0);
  const [templateSlotIds, setTemplateSlotIds] = useState<string[]>(["slot-0"]);
  const templateSlotIdCounter = useRef(1);
  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const [showParentCompaniesOnly, setShowParentCompaniesOnly] =
    useState<boolean>(INITIAL_SHOW_PARENT_COMPANIES_ONLY);
  const [showEmptyValuesOnly, setShowEmptyValuesOnly] =
    useState<boolean>(INITIAL_SHOW_EMPTY_VALUES_ONLY);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [anchorDataValues, setAnchorDataValues] = useState<Record<string, any>>(
    {},
  );
  const [anchorDataTemplateTypes, setAnchorDataTemplateTypes] = useState<
    Record<string, string>
  >({});
  const [anchorDataTemplateDetails, setAnchorDataTemplateDetails] = useState<
    Record<string, any>
  >({});
  const [anchorDataTemplateOptions, setAnchorDataTemplateOptions] = useState<any[]>(
    [],
  );
  const [anchorDataTemplateOptionsLoaded, setAnchorDataTemplateOptionsLoaded] =
    useState(false);
  const [valueMaps, setValueMaps] = useState<Record<string, any[]>>({});
  const [typeMappings, setTypeMappings] = useState<Record<string, any>>({});
  const [objectListFields, setObjectListFields] = useState<
    Record<string, any[]>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [resetToInheritedTemplates, setResetToInheritedTemplates] = useState<
    Record<string, boolean>
  >({});
  const [inheritedResetMeta, setInheritedResetMeta] = useState<
    Record<string, { parentName: string; inheritedValue: any }>
  >({});
  const [columnFilterLabelCache, setColumnFilterLabelCache] = useState<
    Record<string, Record<string, string>>
  >({});
  const [isSubjectRootOfTree, setIsSubjectRootOfTree] = useState(false);
  const [error, setError] = useState<any>();
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [inlineEditMode, setInlineEditMode] = useState(false);
  const [pendingInlineEdits, setPendingInlineEdits] = useState<
    Record<string, Record<string, any>>
  >({});
  const [pendingResetToInherited, setPendingResetToInherited] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [localInlineDirtyCells, setLocalInlineDirtyCells] = useState<
    Record<string, true>
  >({});
  const [invalidEmailCellKeys, setInvalidEmailCellKeys] = useState<
    Record<string, true>
  >({});
  const [invalidPhoneCellKeys, setInvalidPhoneCellKeys] = useState<
    Record<string, true>
  >({});
  const [isSavingInline, setIsSavingInline] = useState(false);
  const [saveAllErrorResult, setSaveAllErrorResult] = useState<{
    successCount: number;
    failureCount: number;
    errors: string[];
  } | null>(null);
  const [enumResetSignals, setEnumResetSignals] = useState<
    Record<string, number>
  >({});
  const incrementEnumResetSignal = useCallback(
    (subjectId: string, columnId: string) => {
      const k = `${subjectId}-${columnId}`;
      setEnumResetSignals((prev) => ({ ...prev, [k]: (prev[k] ?? 0) + 1 }));
    },
    [],
  );
  const [overviewBaseRows, setOverviewBaseRows] = useState<any[]>([]);
  const tableInstanceRef = useRef<{
    setEditingCell: (cell: any) => void;
  } | null>(null);
  const inlineInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const overviewRootRef = useRef<HTMLDivElement | null>(null);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, string>({
      fieldOrder: [],
    });
  const hasHydratedPersistedTemplates = useRef(false);

  const ANCHOR_DATA_TEMPLATES_COLUMNS_MAX = 4;
  const kaerCompanyId = KAER_COMPANY_ID;

  const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    allowSubjectSearchForNonAdmin: false,
  });

  const hasPendingInlineEdits =
    Object.keys(pendingInlineEdits).length > 0 ||
    Object.keys(pendingResetToInherited).length > 0;
  const hasUnflushedInlineChanges =
    Object.keys(localInlineDirtyCells).length > 0;
  const hasAnyInlineChanges =
    inlineEditMode && (hasPendingInlineEdits || hasUnflushedInlineChanges);
  const hasAnyInvalidEmail = Object.keys(invalidEmailCellKeys).length > 0;
  const hasAnyInvalidPhone = Object.keys(invalidPhoneCellKeys).length > 0;
  const hasAnyInvalidField = hasAnyInvalidEmail || hasAnyInvalidPhone;
  const getInvalidCellKeysInDisplayOrder = useCallback(() => {
    const invalidKeys = new Set([
      ...Object.keys(invalidEmailCellKeys),
      ...Object.keys(invalidPhoneCellKeys),
    ]);
    const displayedKeys = Array.from(
      overviewRootRef.current?.querySelectorAll<HTMLElement>(
        "[data-anchor-data-cell-key]",
      ) ?? [],
    )
      .map((element) => element.dataset.anchorDataCellKey)
      .filter((cellKey): cellKey is string => Boolean(cellKey));
    const orderedInvalidKeys = displayedKeys.filter((cellKey) =>
      invalidKeys.delete(cellKey),
    );
    return [...orderedInvalidKeys, ...invalidKeys];
  }, [invalidEmailCellKeys, invalidPhoneCellKeys]);

  const columnKeysWithPendingEdits = useMemo(() => {
    const keys = new Set<string>();
    for (const subjectId of Object.keys(pendingInlineEdits)) {
      for (const columnId of Object.keys(pendingInlineEdits[subjectId] ?? {})) {
        keys.add(columnId);
      }
    }
    for (const subjectId of Object.keys(pendingResetToInherited)) {
      for (const columnId of Object.keys(
        pendingResetToInherited[subjectId] ?? {},
      )) {
        keys.add(columnId);
      }
    }
    for (const cellKey of Object.keys(localInlineDirtyCells)) {
      const [, columnId] = cellKey.split("::");
      if (columnId) {
        keys.add(columnId);
      }
    }
    return keys;
  }, [pendingInlineEdits, pendingResetToInherited, localInlineDirtyCells]);

  const handleLocalInlineDirtyChange = useCallback(
    (subjectId: string, columnId: string, isDirty: boolean) => {
      const cellKey = `${subjectId}::${columnId}`;
      setLocalInlineDirtyCells((prev) => {
        const exists = prev[cellKey] === true;
        if (isDirty && exists) return prev;
        if (!isDirty && !exists) return prev;
        if (isDirty) return { ...prev, [cellKey]: true };
        const next = { ...prev };
        delete next[cellKey];
        return next;
      });
    },
    [],
  );

  const handleEmailValidationChange = useCallback(
    (subjectId: string, columnId: string, isInvalid: boolean) => {
      const cellKey = `${subjectId}::${columnId}`;
      setInvalidEmailCellKeys((prev) => {
        const exists = prev[cellKey] === true;
        if (isInvalid && exists) return prev;
        if (!isInvalid && !exists) return prev;
        if (isInvalid) return { ...prev, [cellKey]: true };
        const next = { ...prev };
        delete next[cellKey];
        return next;
      });
    },
    [],
  );

  const handlePhoneValidationChange = useCallback(
    (subjectId: string, columnId: string, isInvalid: boolean) => {
      const cellKey = `${subjectId}::${columnId}`;
      setInvalidPhoneCellKeys((prev) => {
        const exists = prev[cellKey] === true;
        if (isInvalid && exists) return prev;
        if (!isInvalid && !exists) return prev;
        if (isInvalid) return { ...prev, [cellKey]: true };
        const next = { ...prev };
        delete next[cellKey];
        return next;
      });
    },
    [],
  );

  const handleRemoveLastTemplateColumn = useCallback(() => {
    const freshSlotId = `slot-${templateSlotIdCounter.current++}`;
    setSelectedAnchorDataTemplates([null]);
    setLastSelectedTemplates([null]);
    setTemplateSlotIds([freshSlotId]);
    dispatch(
      clearAnchorDataOverviewTemplateIds({
        entity: ANCHOR_DATA_OVERVIEW_TABLE_CONFIG_ENTITY,
      }),
    );
  }, [dispatch]);

  useConfirmOnLeave(
    hasAnyInlineChanges || isSavingInline,
    "unsaved_formChanges_confirm",
  );
  useRegisterUnsavedChanges(
    "AnchorDataOverviewList",
    hasAnyInlineChanges || isSavingInline,
  );

  useEffect(() => {
    api.genericApiRequest({
      method: "get",
      entity: "anchorDataTemplate",
      parametersToRender: {
        limit: "1",
      },
      successHandler: (res: any) => {
        const count = res.data.count;

        api.genericApiRequest({
          method: "get",
          entity: "anchorDataTemplate",
          parametersToRender: {
            limit: String(count),
          },
          successHandler: (res2: any) => {
            const data = res2.data.results;

            const preparedOptions = data.map((item: any) => ({
              value: String(item.id),
              name: item.name,
              label: item.label + " (" + item.name + ")",
            }));

            setAnchorDataTemplateOptions(preparedOptions);
            setAnchorDataTemplateOptionsLoaded(true);
          },
          failHandler: (fetchErr: any) => {
            setError(fetchErr);
            setAnchorDataTemplateOptionsLoaded(true);
          },
        });
      },
      failHandler: (fetchErr: any) => {
        setError(fetchErr);
        setAnchorDataTemplateOptionsLoaded(true);
      },
    });
  }, []);

  useEffect(() => {
    if (!anchorDataTemplateOptionsLoaded || hasHydratedPersistedTemplates.current) {
      return;
    }
    hasHydratedPersistedTemplates.current = true;

    const optionMap = new Map<string, any>(
      anchorDataTemplateOptions.map((option) => [String(option.value), option]),
    );
    const validPersistedIds = persistedTemplateIds
      .map(String)
      .filter((id, idx, arr) => arr.indexOf(id) === idx)
      .filter((id) => optionMap.has(id))
      .slice(0, ANCHOR_DATA_TEMPLATES_COLUMNS_MAX);

    if (!Array.isArray(persistedTemplateIdsRaw)) {
      setSelectedAnchorDataTemplates([null]);
      setLastSelectedTemplates([null]);
      setTemplateSlotIds(["slot-0"]);
      templateSlotIdCounter.current = 1;
      return;
    }

    if (validPersistedIds.length === 0) {
      setSelectedAnchorDataTemplates([null]);
      setLastSelectedTemplates([null]);
      setTemplateSlotIds(["slot-0"]);
      templateSlotIdCounter.current = 1;
      return;
    }

    const restoredTemplates = validPersistedIds.map((id) => optionMap.get(id));
    setSelectedAnchorDataTemplates(restoredTemplates);
    setLastSelectedTemplates(restoredTemplates);
    setTemplateSlotIds(
      restoredTemplates.map((_, index) => `slot-${index}`),
    );
    templateSlotIdCounter.current = restoredTemplates.length;
  }, [
    anchorDataTemplateOptions,
    anchorDataTemplateOptionsLoaded,
    persistedTemplateIdsRaw,
    persistedTemplateIds,
  ]);

  useEffect(() => {
    if (!anchorDataTemplateOptionsLoaded || !hasHydratedPersistedTemplates.current) {
      return;
    }

    const validTemplateIds = new Set(
      anchorDataTemplateOptions.map((option) => String(option.value)),
    );
    const selectedIds = selectedAnchorDataTemplates
      .map((template, idx) => template || lastSelectedTemplates[idx])
      .filter(Boolean)
      .map((template) => String(template.value))
      .filter((id, idx, arr) => arr.indexOf(id) === idx)
      .filter((id) => validTemplateIds.has(id))
      .slice(0, ANCHOR_DATA_TEMPLATES_COLUMNS_MAX);

    const normalizedPersisted = persistedTemplateIds
      .map(String)
      .filter((id, idx, arr) => arr.indexOf(id) === idx)
      .filter((id) => validTemplateIds.has(id))
      .slice(0, ANCHOR_DATA_TEMPLATES_COLUMNS_MAX);

    if (!Array.isArray(persistedTemplateIdsRaw) && selectedIds.length === 0) {
      return;
    }

    if (
      selectedIds.length === 0 &&
      normalizedPersisted.length > 0 &&
      templateSlotIds.length > 0
    ) {
      return;
    }

    if (JSON.stringify(selectedIds) === JSON.stringify(normalizedPersisted)) {
      return;
    }

    if (selectedIds.length === 0) {
      dispatch(
        clearAnchorDataOverviewTemplateIds({
          entity: ANCHOR_DATA_OVERVIEW_TABLE_CONFIG_ENTITY,
        }),
      );
      return;
    }

    dispatch(
      setAnchorDataOverviewTemplateIds({
        entity: ANCHOR_DATA_OVERVIEW_TABLE_CONFIG_ENTITY,
        templateIds: selectedIds,
      }),
    );
  }, [
    anchorDataTemplateOptions,
    anchorDataTemplateOptionsLoaded,
    dispatch,
    lastSelectedTemplates,
    persistedTemplateIdsRaw,
    persistedTemplateIds,
    selectedAnchorDataTemplates,
    templateSlotIds,
  ]);

  useEffect(() => {
    const activeTemplates = selectedAnchorDataTemplates
      .map((template, idx) => template || lastSelectedTemplates[idx])
      .filter(Boolean);
    fetchTemplateTypes(
      activeTemplates,
      anchorDataTemplateDetails,
      setAnchorDataTemplateDetails,
      setAnchorDataTemplateTypes,
    );
  }, [selectedAnchorDataTemplates, lastSelectedTemplates]);

  const handleEditClick = useCallback(
    async (row: any) => {
      const original = row?.original ?? row;
      setSelectedRow(original);

      if (selectedAnchorDataTemplates.filter(Boolean).length === 1) {
        setAnchorDataValues({
          [selectedAnchorDataTemplates[0].value]:
            selectedAnchorDataTemplates[0].value,
        });
      } else {
        setAnchorDataValues({});
      }

      setValueMaps({});
      setInheritedResetMeta({});
      setEditModalOpen(true);
    },
    [selectedAnchorDataTemplates],
  );

  const columns = useMemo(() => {
    const inlineEditableColumnKeys = selectedAnchorDataTemplates
      .map((t, i) => {
        const d = t || lastSelectedTemplates[i];
        const type = d ? anchorDataTemplateTypes[d.value] : null;
        const enumHasOptions =
          type === "enum" &&
          !!d &&
          (Array.isArray(anchorDataTemplateDetails[d.value]?.allowed_value_set)
            ? anchorDataTemplateDetails[d.value].allowed_value_set.length > 0
            : false);
        return d &&
          (type === "str" ||
            type === "int" ||
            type === "float" ||
            type === "price" ||
            type === "percent" ||
            type === "email" ||
            type === "phone_number" ||
            type === "date" ||
            type === "multiline" ||
            type === "subject_id_list" ||
            type === "wz_code_id_list" ||
            enumHasOptions)
          ? `value_${d.value}`
          : null;
      })
      .filter(Boolean) as string[];
    const orderedEditableCellKeys = overviewBaseRows.flatMap((row) =>
      inlineEditableColumnKeys.map(
        (colKey) => [row.id, colKey] as [string, string],
      ),
    );
    const getNextCell = (
      rowId: string,
      columnId: string,
    ): [string, string] | null => {
      const idx = orderedEditableCellKeys.findIndex(
        ([r, c]) => r === rowId && c === columnId,
      );
      if (idx < 0 || idx >= orderedEditableCellKeys.length - 1) return null;
      return orderedEditableCellKeys[idx + 1];
    };
    const getPrevCell = (
      rowId: string,
      columnId: string,
    ): [string, string] | null => {
      const idx = orderedEditableCellKeys.findIndex(
        ([r, c]) => r === rowId && c === columnId,
      );
      if (idx <= 0) return null;
      return orderedEditableCellKeys[idx - 1];
    };

    const editedInlineEditorSx = {
      minWidth: 0,
      overflowWrap: "anywhere" as const,
    };
    const editedTableCellSx = {
      minWidth: 0,
      overflowWrap: "anywhere" as const,
      px: 1,
      py: 2,
      borderRight: "1px solid #e0e0e0",
      backgroundColor: "rgba(129, 199, 132, 0.25)",
    };

    const cols: Array<any> = [
      {
        id: "company_name",
        header: t("company_name"),
        accessorKey: "name",
        size: 200,
        enableSorting: true,
        enableEditing: false,
        customColumnFilterId: "subject_company_name",
        accessorFn: (row: any) => (
          <span className={styles.companyNameCell}>
            <NavigateToSubjectOverviewLink
              subjectId={row.id}
              label={row.name}
              boldText={!showParentCompaniesOnly && row.is_parent}
            />
            {row.is_draft && <DraftBadge />}
          </span>
        ),
      },
    ];

    selectedAnchorDataTemplates.forEach((template, idx) => {
      const def = template || lastSelectedTemplates[idx];
      if (!def) return;

      const key = `value_${def.value}`;
      const templateType = anchorDataTemplateTypes[def.value];
      const templateDetails = anchorDataTemplateDetails[def.value];
      const normalizedTemplateName = String(
        templateDetails?.name ?? def.name ?? "",
      )
        .trim()
        .toUpperCase();
      const headerLabel =
        templateDetails?.label ??
        String(def.label).replace(/\s*\([^)]*\)\s*$/, "");
      const objectListFieldOrder = Array.isArray(
        templateDetails?.allowed_value_set,
      )
        ? templateDetails.allowed_value_set
          .map((f: any) => (typeof f === "string" ? f : f?.value))
          .filter(Boolean)
        : undefined;

      let customColumnFilterId = "value";
      if (templateType === "subject_id_list") {
        customColumnFilterId = "value_subject_set__id__in";
      } else if (templateType === "wz_code_id_list") {
        customColumnFilterId = "wz_code_set__id__in";
      } else if (normalizedTemplateName === "ANCHOR_DATA_EMAIL") {
        customColumnFilterId = "subject_company_email";
      } else if (normalizedTemplateName === "KUNDENNUMMER") {
        customColumnFilterId = "subject_company_customer_id";
      }

      const enumOptionsForColumn =
        templateType === "enum"
          ? (() => {
            const allowed = templateDetails?.allowed_value_set;
            return Array.isArray(allowed)
              ? allowed.map((item: any) => {
                if (
                  item != null &&
                  typeof item === "object" &&
                  "value" in item
                ) {
                  return {
                    value: String((item as any).value),
                    label:
                      (item as any).label ?? String((item as any).value),
                  };
                }
                const s = String(item);
                return { value: s, label: s };
              })
              : [];
          })()
          : undefined;

      const hasEnumOptions = (enumOptionsForColumn?.length ?? 0) > 0;
      const loadOptionsForSubjectIdListColumn =
        templateType === "subject_id_list"
          ? (input: string) =>
            fetchSubjects(input, {
              entity: "subject/quicksearch",
              queryParameter: "users",
              companyIdForFiltering: kaerCompanyId,
            })
          : undefined;
      const loadOptionsForWzCodeIdListColumn =
        templateType === "wz_code_id_list"
          ? (input: string) =>
            fetchSubjects(input, {
              entity: "wzCode",
              mapResponseToOptions: (data: any) =>
                (data?.results || []).map((item: any) => ({
                  value: item.id,
                  label: `${item.code} - ${item.description}`,
                })),
            }) || []
          : undefined;
      const sharedInlineEditorProps =
        inlineEditMode && templateType === "object_list"
          ? {
            enableEditing: false as const,
            Cell: ({ row }: any) => (
              <AnchorDataOverviewObjectListCellEditor
                row={row}
                columnId={key}
                headerLabel={headerLabel}
                formatObjectListEntries={formatObjectListEntries}
                objectListFieldOrder={objectListFieldOrder}
                onEditClick={handleEditClick}
              />
            ),
          }
          : inlineEditMode &&
            (templateType === "str" ||
              templateType === "int" ||
              templateType === "float" ||
              templateType === "price" ||
              templateType === "percent" ||
              templateType === "email" ||
              templateType === "phone_number" ||
              templateType === "date" ||
              templateType === "multiline" ||
              templateType === "subject_id_list" ||
              templateType === "wz_code_id_list" ||
              (templateType === "enum" && hasEnumOptions))
            ? {
              enableEditing: false as const,
              Cell: ({ row }: any) => (
                <AnchorDataOverviewCellInlineEditor
                  row={row}
                  columnId={key}
                  template={def}
                  templateType={templateType}
                  editedInlineEditorSx={editedInlineEditorSx}
                  overviewBaseRows={overviewBaseRows}
                  pendingInlineEdits={pendingInlineEdits}
                  pendingResetToInherited={pendingResetToInherited}
                  isSavingInline={isSavingInline}
                  setPendingInlineEdits={setPendingInlineEdits}
                  setPendingResetToInherited={setPendingResetToInherited}
                  handleSaveCell={handleSaveCell}
                  handleDiscardCell={handleDiscardCell}
                  getNextCell={getNextCell}
                  getPrevCell={getPrevCell}
                  inlineInputRefs={inlineInputRefs}
                  formatInheritedForDisplay={formatInheritedForDisplay}
                  isSameAnchorDataValue={isSameAnchorDataValue}
                  onLocalDirtyChange={handleLocalInlineDirtyChange}
                  onEmailValidationChange={handleEmailValidationChange}
                  onPhoneValidationChange={handlePhoneValidationChange}
                  validationTargetRef={registerValidationTarget(
                    `${row.original.id}::${key}`,
                  )}
                  enumOptions={enumOptionsForColumn}
                  enumResetSignal={
                    enumResetSignals[`${row.original.id}-${key}`] ?? 0
                  }
                  incrementEnumResetSignal={incrementEnumResetSignal}
                  loadOptionsForSubjectIdList={
                    loadOptionsForSubjectIdListColumn
                  }
                  loadOptionsForWzCodeIdList={loadOptionsForWzCodeIdListColumn}
                />
              ),
            }
            : {
              enableEditing:
                inlineEditMode &&
                (templateType === "str" ||
                  templateType === "int" ||
                  templateType === "float" ||
                  templateType === "price" ||
                  templateType === "percent" ||
                  templateType === "email" ||
                  templateType === "phone_number" ||
                  templateType === "date" ||
                  templateType === "multiline" ||
                  templateType === "subject_id_list" ||
                  templateType === "wz_code_id_list" ||
                  (templateType === "enum" && hasEnumOptions)),
            };

      cols.push({
        id: key,
        header: headerLabel,
        accessorKey: key,
        enableSorting: true,
        customSortingKey: def.value,
        ...sharedInlineEditorProps,
        customColumnFilterId,
        setNoFilterParameter:
          templateType === "subject_id_list" ||
          templateType === "wz_code_id_list",
        ...(templateType === "date" && getAnchorDataDateFilterOptions(def.value)),
        ...(templateType === "percent" && {
          filterVariant: "percent" as const,
        }),
        ...(templateType === "price" && {
          filterVariant: "price" as const,
        }),
        ...(templateType === "enum" &&
          (() => {
            const allowed = templateDetails?.allowed_value_set;
            const options = Array.isArray(allowed)
              ? allowed.map((item: any) => {
                if (
                  item != null &&
                  typeof item === "object" &&
                  "value" in item
                ) {
                  return {
                    value: String((item as any).value),
                    label: (item as any).label ?? String((item as any).value),
                  };
                }
                const s = String(item);
                return { value: s, label: s };
              })
              : [];
            return options.length > 0
              ? {
                filterVariant: "select" as const,
                filterSelectOptions: options,
              }
              : {};
          })()),
        Filter:
          templateType === "subject_id_list"
            ? ({ column }: any) => {
              const filterValue = column.getFilterValue();
              const selectedIds =
                typeof filterValue === "string" && filterValue !== ""
                  ? filterValue
                    .split(",")
                    .map((id: string) => id.trim())
                    .filter(Boolean)
                  : [];
              const preExistingValue =
                selectedIds.length > 0
                  ? selectedIds.map((id: string) => ({
                    value: id,
                    label: columnFilterLabelCache[key]?.[id] ?? id,
                  }))
                  : undefined;
              return (
                <SubjectSearchSelect
                  lookLikeTextField
                  isMulti={true}
                  isClearable={true}
                  placeholder={t(
                    "filter_by_anchor_data_template_placeholder",
                    { templateLabel: headerLabel },
                  )}
                  value={preExistingValue || []}
                  loadOptions={(input: string) =>
                    fetchSubjects(input, {
                      entity: "subject/quicksearch",
                      queryParameter: "users",
                      companyIdForFiltering: kaerCompanyId,
                    })
                  }
                  onChange={(selectedOption: any) => {
                    const currentFilterValue = String(
                      column.getFilterValue() ?? "",
                    );
                    const selected = Array.isArray(selectedOption)
                      ? selectedOption
                      : selectedOption
                        ? [selectedOption]
                        : [];
                    const subjectIds = selected
                      .map((opt: any) => opt?.value)
                      .filter(Boolean)
                      .map(String);
                    const nextLabelMap = {
                      ...(columnFilterLabelCache[key] || {}),
                    };
                    selected.forEach((opt: any) => {
                      if (opt?.value) {
                        nextLabelMap[String(opt.value)] = String(
                          opt.label ?? opt.value,
                        );
                      }
                    });
                    setColumnFilterLabelCache((prev) => ({
                      ...prev,
                      [key]: nextLabelMap,
                    }));
                    const nextFilterValue = subjectIds.join(",");
                    if (nextFilterValue !== currentFilterValue) {
                      column.setFilterValue(nextFilterValue);
                    }
                  }}
                />
              );
            }
            : templateType === "wz_code_id_list"
              ? ({ column }: any) => {
                const filterValue = column.getFilterValue();
                const selectedIds =
                  typeof filterValue === "string" && filterValue !== ""
                    ? filterValue
                      .split(",")
                      .map((id: string) => id.trim())
                      .filter(Boolean)
                    : [];
                const selectedValue = selectedIds.map((id: string) => ({
                  value: id,
                  label: columnFilterLabelCache[key]?.[id] ?? id,
                }));

                return (
                  <SubjectSearchSelect
                    lookLikeTextField
                    isMulti={true}
                    isClearable={true}
                    placeholder={t(
                      "filter_by_anchor_data_template_placeholder",
                      {
                        templateLabel: headerLabel,
                      },
                    )}
                    value={selectedValue}
                    onChange={(selectedOption: any) => {
                      const currentFilterValue = String(
                        column.getFilterValue() ?? "",
                      );
                      const selected = Array.isArray(selectedOption)
                        ? selectedOption
                        : selectedOption
                          ? [selectedOption]
                          : [];
                      const wzCodeIds = selected
                        .map((opt: any) => opt?.value)
                        .filter(Boolean)
                        .map(String);
                      const nextLabelMap = {
                        ...(columnFilterLabelCache[key] || {}),
                      };
                      selected.forEach((opt: any) => {
                        if (opt?.value) {
                          nextLabelMap[String(opt.value)] = String(
                            opt.label ?? opt.value,
                          );
                        }
                      });
                      setColumnFilterLabelCache((prev) => ({
                        ...prev,
                        [key]: nextLabelMap,
                      }));
                      const nextFilterValue = wzCodeIds.join(",");
                      if (nextFilterValue !== currentFilterValue) {
                        column.setFilterValue(nextFilterValue);
                      }
                    }}
                    loadOptions={(input: string) =>
                      fetchSubjects(input, {
                        entity: "wzCode",
                        mapResponseToOptions: (data: any) =>
                          (data?.results || []).map((item: any) => ({
                            value: item.id,
                            label: `${item.code} - ${item.description}`,
                          })),
                      }) || []
                    }
                  />
                );
              }
              : undefined,
        accessorFn: (row: any) => {
          const value =
            inlineEditMode && pendingInlineEdits[row.id]?.[key] !== undefined
              ? pendingInlineEdits[row.id][key]
              : row[key];
          const isInherited = row[`is_inherited_${def.value}`];
          const inheritedFrom = row[`inherited_from_${def.value}`];
          const hasPendingEdit =
            inlineEditMode && pendingInlineEdits[row.id]?.[key] !== undefined;
          const isPendingReset = !!pendingResetToInherited[row.id]?.[key];
          const useInheritedStyle =
            (isInherited && !hasPendingEdit) ||
            (hasPendingEdit && isPendingReset);
          const wrapValueField = (content: React.ReactNode) => {
            const inner = !useInheritedStyle ? (
              content
            ) : (
              <Box component="span" className={styles.inheritedValueWrapper}>
                <Tooltip
                  placement="top"
                  arrow
                  title={
                    <div className={styles.tooltipContent}>
                      <span>{headerLabel}</span>
                      <br />
                      <br />
                      <span className={styles.labelinheritedInfo}>
                        {t("inherited_value")}
                      </span>
                      <br />
                      <span className={styles.labelinheritedInfo}>
                        {t("inherited_from")}:{" "}
                      </span>
                      {inheritedFrom?.name ?? "-"}
                    </div>
                  }
                >
                  <span className={styles.inheritedIconTable}>ⓘ</span>
                </Tooltip>
                <span className={styles.inheritedValueText}>{content}</span>
              </Box>
            );
            return inner;
          };

          if (templateType === "subject_id_list") {
            if (Array.isArray(value)) {
              const names = value
                .map((v: any) => {
                  if (!v) return null;
                  if (typeof v === "object")
                    return v.name ?? v.label ?? v.value ?? v.id ?? null;
                  return v;
                })
                .filter(Boolean)
                .map(String);
              return wrapValueField(names.length > 0 ? names.join(", ") : "-");
            }
            if (value && typeof value === "object") {
              const n =
                (value as any).name ??
                (value as any).label ??
                (value as any).value ??
                (value as any).id;
              return wrapValueField(n ? String(n) : "-");
            }
          }

          if (templateType === "wz_code_id_list") {
            const labelFor = (v: any) => {
              if (!v) return null;
              if (typeof v === "object") {
                if ("label" in v) return (v as any).label;
                const code =
                  (v as any).code ?? (v as any).name ?? (v as any).id;
                const desc = (v as any).description ?? "";
                return desc ? `${code} - ${desc}` : code;
              }
              return v;
            };
            if (Array.isArray(value)) {
              const labels = value.map(labelFor).filter(Boolean).map(String);
              if (labels.length === 0) return wrapValueField("-");
              return wrapValueField(
                <>
                  {labels.map((entry, entryIdx) => (
                    <span key={entryIdx}>
                      {entry}
                      {entryIdx < labels.length - 1 && <br />}
                    </span>
                  ))}
                </>,
              );
            }
            if (value && typeof value === "object") {
              const l = labelFor(value);
              return wrapValueField(l ? String(l) : "-");
            }
          }

          if (
            Array.isArray(value) &&
            value.length > 0 &&
            typeof value[0] === "object" &&
            value[0] !== null
          ) {
            return wrapValueField(
              formatObjectListEntries(value, objectListFieldOrder, t),
            );
          }

          if (
            typeof value === "string" &&
            value.trim().startsWith("[") &&
            value.trim().endsWith("]")
          ) {
            try {
              const arr = JSON.parse(value.replace(/'/g, '"'));
              if (Array.isArray(arr)) {
                if (
                  arr.length > 0 &&
                  typeof arr[0] === "object" &&
                  arr[0] !== null
                ) {
                  return wrapValueField(
                    formatObjectListEntries(arr, objectListFieldOrder, t),
                  );
                }
                return wrapValueField(arr.join(", "));
              }
            } catch {
              // fall-through
            }
          }
          if (Array.isArray(value)) {
            if (
              value.length > 0 &&
              typeof value[0] === "object" &&
              value[0] !== null
            ) {
              return wrapValueField(
                formatObjectListEntries(value, objectListFieldOrder, t),
              );
            }
            return wrapValueField(value.join(", "));
          }

          if (templateType === "enum" && value != null && value !== "") {
            const allowed = templateDetails?.allowed_value_set;
            if (Array.isArray(allowed)) {
              const match = allowed.find((item: any) => {
                if (item && typeof item === "object" && "value" in item) {
                  return String(item.value) === String(value);
                }
                if (typeof item === "string") {
                  return item === String(value);
                }
                return false;
              });
              if (match && typeof match === "object" && "label" in match) {
                return wrapValueField(String((match as any).label));
              }
            }
          }

          if (
            (templateType === "float" || templateType === "price") &&
            (typeof value === "number" ||
              (typeof value === "string" && !isNaN(Number(value))))
          ) {
            const numValue =
              typeof value === "number" ? value : parseFloat(value);
            if (!isNaN(numValue)) {
              return wrapValueField(String(numValue).replace(".", ","));
            }
          }

          if (templateType === "date") {
            const displayValue = formatGermanDate(value);
            if (displayValue != null) return wrapValueField(displayValue);
          }

          if (value == null) {
            return wrapValueField("-");
          }
          if (
            value === "" ||
            (typeof value === "number" && Number.isNaN(value))
          ) {
            return wrapValueField(" ");
          }
          return wrapValueField(value);
        },
        muiTableBodyCellProps: ({ row }: any) => {
          if (
            templateType === "str" ||
            templateType === "int" ||
            templateType === "float" ||
            templateType === "price" ||
            templateType === "percent" ||
            templateType === "email" ||
            templateType === "phone_number" ||
            templateType === "date" ||
            templateType === "multiline" ||
            templateType === "subject_id_list" ||
            templateType === "wz_code_id_list" ||
            templateType === "object_list" ||
            (templateType === "enum" && hasEnumOptions)
          ) {
            return {
              sx: {
                borderRight: "1px solid #e0e0e0",
                "&:has(.anchordata-inline-edited-cell)": {
                  backgroundColor: "rgba(129, 199, 132, 0.25)",
                },
              },
            };
          }
          const hasPending =
            inlineEditMode &&
            (pendingInlineEdits[row.original?.id]?.[key] !== undefined ||
              pendingResetToInherited[row.original?.id]?.[key]);
          if (!hasPending) return {};
          return { sx: editedTableCellSx };
        },
      });
    });

    return cols;
  }, [
    selectedAnchorDataTemplates,
    lastSelectedTemplates,
    anchorDataTemplateTypes,
    anchorDataTemplateDetails,
    columnFilterLabelCache,
    t,
    inlineEditMode,
    pendingInlineEdits,
    pendingResetToInherited,
    overviewBaseRows,
    enumResetSignals,
    incrementEnumResetSignal,
    fetchSubjects,
    kaerCompanyId,
    handleEditClick,
    showParentCompaniesOnly,
  ]);

  const getErrorMessage = useCallback((err: any): string => {
    if (err == null) return "";
    if (typeof err === "string") return err;
    if (typeof err === "number" || typeof err === "boolean") return String(err);
    const detail = err?.response?.data?.detail;
    if (detail != null)
      return typeof detail === "string" ? detail : JSON.stringify(detail);
    if (err?.response?.data?.message) return String(err.response.data.message);
    if (err?.message) return err.message;
    if (typeof err === "object") {
      if (typeof err.detail === "string") return err.detail;
      if (typeof err.message === "string") return err.message;
      const entries = Object.entries(err);
      if (entries.length === 0) return "";

      const formatVal = (v: any): string => {
        if (typeof v === "string") return v;
        if (Array.isArray(v))
          return v
            .map((x: any) =>
              typeof x === "object" && x !== null
                ? JSON.stringify(x)
                : String(x),
            )
            .join("; ");
        if (typeof v === "object" && v !== null) return JSON.stringify(v);
        return String(v);
      };

      const isTemplateErrorValue = (v: any): v is { value: any[] } =>
        typeof v === "object" &&
        v !== null &&
        "value" in v &&
        Array.isArray((v as { value: any[] }).value);

      const formatTemplateErrorEntry = (
        templateName: string,
        v: any,
      ): string => {
        if (!isTemplateErrorValue(v)) return `${templateName}: ${formatVal(v)}`;
        const messages = (v.value as any[]).map((x: any) =>
          typeof x === "string" ? x : String(x),
        );
        return `${templateName}: ${messages.join("; ")}`;
      };

      if (entries.length === 1) {
        const [k, v] = entries[0];
        return formatTemplateErrorEntry(k, v);
      }
      const parts = entries.map(([k, v]) => formatTemplateErrorEntry(k, v));
      return parts.join(" · ");
    }
    return JSON.stringify(err);
  }, []);

  const handleSaveInlineEdits = useCallback(async () => {
    const invalidCellKeys = getInvalidCellKeysInDisplayOrder();
    if (invalidCellKeys.length > 0) {
      focusFirstInvalidField({}, invalidCellKeys);
      return;
    }

    tableInstanceRef.current?.setEditingCell(null);
    const subjectIds = Array.from(
      new Set([
        ...Object.keys(pendingInlineEdits),
        ...Object.keys(pendingResetToInherited),
      ]),
    );
    if (subjectIds.length === 0) return;
    setIsSavingInline(true);
    setSaveAllErrorResult(null);
    const companyNameById = new Map<string, string>(
      overviewBaseRows.map((row: any) => [row.id, row.name]),
    );

    const columnKeysBySubject: Record<string, string[]> = {};
    const savePayload = subjectIds
      .map((subjectId) => {
        const edits = pendingInlineEdits[subjectId] ?? {};
        const resets = pendingResetToInherited[subjectId] ?? {};
        const columnKeys = Array.from(
          new Set([...Object.keys(edits), ...Object.keys(resets)]),
        );
        columnKeysBySubject[subjectId] = columnKeys;
        if (columnKeys.length === 0) return null;

        const anchorDataValuesPayload: Record<string, any> = {};
        for (const columnKey of columnKeys) {
          const templateId = columnKey.replace(/^value_/, "");
          const templateName =
            anchorDataTemplateDetails[templateId]?.name ?? templateId;
          const templateType = anchorDataTemplateTypes[templateId] ?? "str";
          if (resets[columnKey]) {
            anchorDataValuesPayload[templateName] = null;
          } else {
            const value = edits[columnKey];
            if (
              templateType === "subject_id_list" ||
              templateType === "wz_code_id_list"
            ) {
              const arr = Array.isArray(value) ? value : [];
              const ids = arr
                .map((v: any) =>
                  typeof v === "object"
                    ? v?.value ?? v?.id ?? String(v)
                    : String(v),
                )
                .filter(Boolean);
              anchorDataValuesPayload[templateName] = { value: ids };
            } else if (templateType === "str" && value === "") {
              anchorDataValuesPayload[templateName] = { value: "" };
            } else {
              anchorDataValuesPayload[templateName] =
                value === "" || value == null ? null : { value };
            }
          }
        }

        if (Object.keys(anchorDataValuesPayload).length === 0) return null;
        return {
          subject: subjectId,
          anchor_data_values: anchorDataValuesPayload,
        };
      })
      .filter(Boolean) as Array<{
        subject: string;
        anchor_data_values: Record<string, any>;
      }>;

    if (savePayload.length === 0) {
      setIsSavingInline(false);
      return;
    }

    const clearCellKeysForSubject = (
      setter: React.Dispatch<React.SetStateAction<Record<string, true>>>,
      subjectId: string,
      columnKeys: string[],
    ) => {
      setter((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const columnKey of columnKeys) {
          const cellKey = `${subjectId}::${columnKey}`;
          if (cellKey in next) {
            delete next[cellKey];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };

    const markSubjectAsSaved = (subjectId: string) => {
      const edits = pendingInlineEdits[subjectId] ?? {};
      const resets = pendingResetToInherited[subjectId] ?? {};
      const columnKeys = columnKeysBySubject[subjectId] ?? [];
      if (columnKeys.length === 0) return;

      setOverviewBaseRows((prev) =>
        applyInlineEditsToOverviewRows(
          prev,
          [subjectId],
          { [subjectId]: edits },
          { [subjectId]: resets },
        ),
      );
      setPendingInlineEdits((prev) => {
        if (!(subjectId in prev)) return prev;
        const next = { ...prev };
        delete next[subjectId];
        return next;
      });
      setPendingResetToInherited((prev) => {
        if (!(subjectId in prev)) return prev;
        const next = { ...prev };
        delete next[subjectId];
        return next;
      });
      clearCellKeysForSubject(setLocalInlineDirtyCells, subjectId, columnKeys);
      clearCellKeysForSubject(setInvalidEmailCellKeys, subjectId, columnKeys);
      clearCellKeysForSubject(setInvalidPhoneCellKeys, subjectId, columnKeys);
    };

    try {
      const response = await new Promise<any>((resolve, reject) => {
        api.genericApiRequest({
          method: "post",
          entity: "anchorData",
          additionalRouteParts: { bulk_create_update: "/" },
          parametersToRender: {},
          submitData: savePayload,
          successHandler: (res: any) => resolve(res),
          failHandler: (err: any) => reject(err),
        });
      });

      const responseData = response?.data ?? {};
      const resultRows = Array.isArray(responseData.results)
        ? responseData.results
        : [];
      const failedResults = resultRows.filter(
        (result: any) => result?.status !== "success",
      );
      const successfulSubjectIds =
        resultRows.length === 0
          ? savePayload.map((entry) => entry.subject)
          : resultRows
            .filter(
              (result: any) =>
                result?.status === "success" && result?.subject,
            )
            .map((result: any) => String(result.subject));
      const failedSubjectIds = new Set<string>(
        failedResults
          .map((result: any) => result?.subject)
          .filter(Boolean)
          .map(String),
      );
      const missingResultSubjectIds = savePayload
        .map((entry) => entry.subject)
        .filter(
          (subjectId) =>
            !successfulSubjectIds.includes(subjectId) &&
            !failedSubjectIds.has(subjectId),
        );
      const collectedErrors = getAnchorDataBulkCreateUpdateErrors(response, {
        submittedSubjectIds: savePayload.map((entry) => entry.subject),
        subjectNameById: companyNameById,
      });

      successfulSubjectIds.forEach(markSubjectAsSaved);

      if (collectedErrors.length > 0) {
        const summary = responseData.summary ?? {};
        setSaveAllErrorResult({
          successCount:
            typeof summary.successful_fields === "number"
              ? summary.successful_fields
              : successfulSubjectIds.reduce(
                (count: number, subjectId: string) =>
                  count + (columnKeysBySubject[subjectId]?.length ?? 0),
                0,
              ),
          failureCount:
            typeof summary.failed_fields === "number"
              ? summary.failed_fields
              : Array.from(failedSubjectIds)
                .concat(missingResultSubjectIds)
                .reduce(
                  (count: number, subjectId: string) =>
                    count + (columnKeysBySubject[subjectId]?.length ?? 0),
                  0,
                ),
          errors: collectedErrors,
        });
        if (successfulSubjectIds.length > 0) {
          setRefreshTable((prev) => !prev);
        }
      } else {
        setPendingInlineEdits({});
        setPendingResetToInherited({});
        setLocalInlineDirtyCells({});
        setInvalidEmailCellKeys({});
        setInvalidPhoneCellKeys({});
        setRefreshTable((prev) => !prev);
      }
    } catch (err) {
      const fieldCount = Object.values(columnKeysBySubject).reduce(
        (count, columnKeys) => count + columnKeys.length,
        0,
      );
      setSaveAllErrorResult({
        successCount: 0,
        failureCount: fieldCount,
        errors: savePayload.map(({ subject }) => {
          const companyName = companyNameById.get(subject) ?? subject;
          const detail = getErrorMessage(err).split(" · ").join("\n");
          return `${companyName}:\n${detail}`;
        }),
      });
    } finally {
      setIsSavingInline(false);
    }
  }, [
    pendingInlineEdits,
    pendingResetToInherited,
    anchorDataTemplateDetails,
    anchorDataTemplateTypes,
    focusFirstInvalidField,
    getInvalidCellKeysInDisplayOrder,
    getErrorMessage,
    overviewBaseRows,
  ]);

  const handleResetInlineEdits = useCallback(async () => {
    tableInstanceRef.current?.setEditingCell(null);
    const confirmReset = await showConfirmationModalAsync(
      t("reset_form_confirm"),
    );
    if (!confirmReset) return;
    setPendingInlineEdits({});
    setPendingResetToInherited({});
    setLocalInlineDirtyCells({});
    setInvalidEmailCellKeys({});
    setInvalidPhoneCellKeys({});
  }, [showConfirmationModalAsync, t]);

  const handleSaveCell = useCallback(
    async (
      subjectId: string,
      columnId: string,
      override?: { value: any; resetToInherited: boolean },
    ) => {
      const cellKey = `${subjectId}::${columnId}`;
      if (
        invalidEmailCellKeys[cellKey] ||
        invalidPhoneCellKeys[cellKey]
      ) {
        focusFirstInvalidField({}, [cellKey]);
        return;
      }

      const edits = pendingInlineEdits[subjectId] ?? {};
      const resets = pendingResetToInherited[subjectId] ?? {};
      const hasEdit = override ? true : edits[columnId] !== undefined;
      const hasReset = override ? override.resetToInherited : resets[columnId];
      if (!hasEdit && !hasReset) return;
      tableInstanceRef.current?.setEditingCell(null);
      setIsSavingInline(true);
      try {
        const templateId = columnId.replace(/^value_/, "");
        const templateName =
          anchorDataTemplateDetails[templateId]?.name ?? templateId;
        const templateType = anchorDataTemplateTypes[templateId] ?? "str";
        const anchorDataValuesPayload: Record<string, any> = {};
        if (hasReset) {
          anchorDataValuesPayload[templateName] = null;
        } else {
          const value = override ? override.value : edits[columnId];
          if (
            templateType === "subject_id_list" ||
            templateType === "wz_code_id_list"
          ) {
            const arr = Array.isArray(value) ? value : [];
            const ids = arr
              .map((v: any) =>
                typeof v === "object"
                  ? v?.value ?? v?.id ?? String(v)
                  : String(v),
              )
              .filter(Boolean);
            anchorDataValuesPayload[templateName] = { value: ids };
          } else if (templateType === "str" && value === "") {
            anchorDataValuesPayload[templateName] = { value: "" };
          } else {
            anchorDataValuesPayload[templateName] =
              value === "" || value == null ? null : { value };
          }
        }
        await new Promise<void>((resolve, reject) => {
          api.genericApiRequest({
            method: "post",
            entity: "anchorData",
            additionalRouteParts: { bulk_create_update: "/" },
            parametersToRender: {},
            submitData: [{
              subject: subjectId,
              anchor_data_values: anchorDataValuesPayload,
            }],
            successHandler: () => resolve(),
            failHandler: (err: any) => reject(err),
          });
        });
        setPendingInlineEdits((prev) => {
          const rowEdits = { ...(prev[subjectId] ?? {}) };
          delete rowEdits[columnId];
          const next = { ...prev };
          if (Object.keys(rowEdits).length === 0) delete next[subjectId];
          else next[subjectId] = rowEdits;
          return next;
        });
        setPendingResetToInherited((prev) => {
          const rowResets = { ...(prev[subjectId] ?? {}) };
          delete rowResets[columnId];
          const next = { ...prev };
          if (Object.keys(rowResets).length === 0) delete next[subjectId];
          else next[subjectId] = rowResets;
          return next;
        });
        setLocalInlineDirtyCells((prev) => {
          const next = { ...prev };
          delete next[`${subjectId}::${columnId}`];
          return next;
        });
        setInvalidEmailCellKeys((prev) => {
          const next = { ...prev };
          delete next[`${subjectId}::${columnId}`];
          return next;
        });
        setInvalidPhoneCellKeys((prev) => {
          const next = { ...prev };
          delete next[`${subjectId}::${columnId}`];
          return next;
        });
        setOverviewBaseRows((prev) =>
          applyInlineEditsToOverviewRows(
            prev,
            [subjectId],
            hasReset
              ? {}
              : {
                [subjectId]: {
                  [columnId]: override ? override.value : edits[columnId],
                },
              },
            hasReset ? { [subjectId]: { [columnId]: true } } : {},
          ),
        );
        setRefreshTable((prev) => !prev);
      } catch (err) {
        setError(err);
      } finally {
        setIsSavingInline(false);
      }
    },
    [
      pendingInlineEdits,
      pendingResetToInherited,
      anchorDataTemplateDetails,
      anchorDataTemplateTypes,
      focusFirstInvalidField,
      invalidEmailCellKeys,
      invalidPhoneCellKeys,
    ],
  );

  const handleDiscardCell = useCallback(
    (subjectId: string, columnId: string) => {
      tableInstanceRef.current?.setEditingCell(null);
      setPendingInlineEdits((prev) => {
        const rowEdits = { ...(prev[subjectId] ?? {}) };
        delete rowEdits[columnId];
        const next = { ...prev };
        if (Object.keys(rowEdits).length === 0) delete next[subjectId];
        else next[subjectId] = rowEdits;
        return next;
      });
      setPendingResetToInherited((prev) => {
        const rowResets = { ...(prev[subjectId] ?? {}) };
        delete rowResets[columnId];
        const next = { ...prev };
        if (Object.keys(rowResets).length === 0) delete next[subjectId];
        else next[subjectId] = rowResets;
        return next;
      });
      setLocalInlineDirtyCells((prev) => {
        const next = { ...prev };
        delete next[`${subjectId}::${columnId}`];
        return next;
      });
      setInvalidEmailCellKeys((prev) => {
        const next = { ...prev };
        delete next[`${subjectId}::${columnId}`];
        return next;
      });
      setInvalidPhoneCellKeys((prev) => {
        const next = { ...prev };
        delete next[`${subjectId}::${columnId}`];
        return next;
      });
    },
    [],
  );

  const handleToggleInlineEditMode = useCallback(async () => {
    if (hasAnyInlineChanges) {
      const confirmDiscard = await showConfirmationModalAsync(
        t("unsaved_formChanges_confirm"),
      );
      if (!confirmDiscard) return;
      setPendingInlineEdits({});
      setPendingResetToInherited({});
      setLocalInlineDirtyCells({});
      setInvalidEmailCellKeys({});
      setInvalidPhoneCellKeys({});
    }
    setInlineEditMode((prev) => !prev);
  }, [hasAnyInlineChanges, showConfirmationModalAsync, t]);

  const inlineEditToolbarElements = useMemo(
    () => (
      <Box className={styles.inlineEditToolbarElements}>
        <GenericButton
          variant="contained"
          color="secondary"
          startIcon={<EditIcon />}
          onClick={handleToggleInlineEditMode}
        >
          {inlineEditMode ? t("deactivate_edit_mode") : t("activate_edit_mode")}
        </GenericButton>
        <FormControlLabel
          label={
            showParentCompaniesOnly
              ? t("company_show_parent_only")
              : t("company_show_all_hierarchy")
          }
          control={
            <Switch
              checked={showParentCompaniesOnly}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setShowParentCompaniesOnly(event.target.checked)
              }
              sx={{
                "& .MuiSwitch-switchBase": {
                  color: "#8c1ec8",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#8c1ec8",
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#ff6428",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#ff6428",
                  },
                },
              }}
            />
          }
        />
        <FormControlLabel
          label={
            showEmptyValuesOnly
              ? t("show_empty_anchor_data_values_only")
              : t("show_all_anchor_data_values")
          }
          control={
            <Switch
              checked={showEmptyValuesOnly}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setShowEmptyValuesOnly(event.target.checked)
              }
              sx={{
                "& .MuiSwitch-switchBase": {
                  color: "#8c1ec8",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#8c1ec8",
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#ff6428",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#ff6428",
                  },
                },
              }}
            />
          }
        />
      </Box>
    ),
    [
      inlineEditMode,
      handleToggleInlineEditMode,
      showParentCompaniesOnly,
      showEmptyValuesOnly,
      t,
    ],
  );

  const inlineEditFloatingButtons = inlineEditMode && (
    <Box className={styles.inlineEditFloatingButtonsContainer}>
      <Box className={styles.inlineEditFloatingResetAllButtonContainer}>
        <GenericButton
          variant="outlined"
          startIcon={<RestoreIcon />}
          disabled={!hasAnyInlineChanges}
          onClick={handleResetInlineEdits}
        >
          {t("reset_all")}
        </GenericButton>
      </Box>
      <Box className={styles.inlineEditFloatingSaveAllButtonContainer}>
        <Tooltip
          title={hasAnyInvalidField ? t("save_all_validation_tooltip") : ""}
          placement="top"
          arrow
        >
          <span
            className={styles.saveAllButtonContainer}
          >
            <GenericButton
              variant="contained"
              startIcon={<SaveIcon />}
              waitingForResponse
              showSpinnerOnDisable
              disabled={isSavingInline || !hasAnyInlineChanges}
              onClick={handleSaveInlineEdits}
            >
              {t("save_all")}
            </GenericButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  const displayDataTransformMemo = useCallback(
    (rows: any[]) =>
      rows.map((row: any) => ({
        ...row,
        ...(pendingInlineEdits[row.id] ?? {}),
      })),
    [pendingInlineEdits],
  );

  const fetchEditDefDetails = useCallback(
    async (template: any, row: any) => {
      if (!template || !row) return;
      const valueKey = `value_${template.value}`;
      const initial = row[valueKey] ?? "";
      let parsed = initial;
      if (
        typeof initial === "string" &&
        initial.trim().startsWith("[") &&
        initial.trim().endsWith("]")
      ) {
        try {
          parsed = JSON.parse(initial.replace(/'/g, '"'));
        } catch {
          parsed = [];
        }
      }

      setAnchorDataValues((prev) => ({
        ...prev,
        [template.value]: parsed,
      }));

      const applyTemplateDetails = (data: any) => {
        const type = data.type;
        setAnchorDataTemplateTypes((prev) => ({
          ...prev,
          [template.value]: type,
        }));

        if (type === "enum") {
          const vm = Array.isArray(data.allowed_value_set)
            ? data.allowed_value_set
            : [];
          setValueMaps((prev) => ({
            ...prev,
            [template.value]: vm,
          }));
          setAnchorDataValues((prev) => ({
            ...prev,
            [template.value]: String(parsed),
          }));
        } else if (type === "object_list") {
          const allowedValueSet = Array.isArray(data.allowed_value_set)
            ? data.allowed_value_set
            : [];
          const typeMapping = data.type_mapping || {};

          setValueMaps((prev) => ({
            ...prev,
            [template.value]: allowedValueSet,
          }));
          setTypeMappings((prev) => ({
            ...prev,
            [template.value]: typeMapping,
          }));

          let objectListData: any[] = [];
          if (Array.isArray(parsed) && parsed.length > 0) {
            objectListData = parsed;
          } else if (
            parsed &&
            typeof parsed === "object" &&
            Object.keys(parsed).length > 0
          ) {
            objectListData = [parsed];
          }

          if (objectListData.length === 0) {
            const emptyObj: any = {};
            allowedValueSet.forEach((field: any) => {
              const fieldName = typeof field === "string" ? field : field.value;
              emptyObj[fieldName] = "";
            });
            objectListData = [emptyObj];
          }

          setObjectListFields((prev) => ({
            ...prev,
            [template.value]: objectListData,
          }));
        } else if (type === "subject_id_list") {
          const toOption = (s: any) => {
            if (!s) return null;
            if (typeof s === "object" && "value" in s && "label" in s) return s;
            if (typeof s === "object" && "id" in s) {
              const v = String((s as any).id);
              const l =
                (s as any).name != null
                  ? String((s as any).name)
                  : (s as any).label != null
                    ? String((s as any).label)
                    : v;
              return { value: v, label: l };
            }
            return { value: String(s), label: String(s) };
          };

          let subjectList: any[] = [];
          if (Array.isArray(parsed)) {
            subjectList = parsed.map(toOption).filter(Boolean);
          } else if (parsed && typeof parsed === "object") {
            const opt = toOption(parsed);
            subjectList = opt ? [opt] : [];
          } else if (parsed) {
            subjectList = [toOption(parsed)].filter(Boolean);
          }
          setAnchorDataValues((prev) => ({
            ...prev,
            [template.value]: subjectList,
          }));
        } else if (type === "wz_code_id_list") {
          const toOption = (w: any) => {
            if (!w) return null;
            if (typeof w === "object" && "value" in w && "label" in w) return w;
            if (typeof w === "object" && "id" in w) {
              const code = (w as any).code ?? (w as any).name ?? (w as any).id;
              const desc = (w as any).description ?? "";
              const label = desc ? `${code} - ${desc}` : String(code);
              return { value: String((w as any).id), label };
            }
            return { value: String(w), label: String(w) };
          };

          let wzCodeList: any[] = [];
          if (Array.isArray(parsed)) {
            wzCodeList = parsed.map(toOption).filter(Boolean);
          } else if (parsed && typeof parsed === "object") {
            const opt = toOption(parsed);
            wzCodeList = opt ? [opt] : [];
          } else if (parsed) {
            wzCodeList = [toOption(parsed)].filter(Boolean);
          }
          setAnchorDataValues((prev) => ({
            ...prev,
            [template.value]: wzCodeList,
          }));
        }
      };

      try {
        const cached = anchorDataTemplateDetails[template.value];
        if (cached) {
          applyTemplateDetails(cached);
          return;
        }

        await api.genericApiRequest({
          method: "get",
          entity: `anchorDataTemplate/${template.value}`,
          parametersToRender: {
            depth: "0",
          },
          successHandler: (resp: any) => {
            setAnchorDataTemplateDetails((prev) => ({
              ...prev,
              [template.value]: resp.data,
            }));
            applyTemplateDetails(resp.data);
          },
          failHandler: (err: any) => setError(err),
        });
      } catch (err) {
        setError(err);
      }
    },
    [anchorDataTemplateDetails],
  );

  useEffect(() => {
    if (editModalOpen && selectedRow) {
      setAnchorDataValues({});
      setValueMaps({});
      setTypeMappings({});
      setObjectListFields({});
      setResetToInheritedTemplates({});

      const activeTemplates = selectedAnchorDataTemplates.filter(Boolean);
      activeTemplates.forEach((template) => {
        if (template) {
          fetchEditDefDetails(template, selectedRow);
        }
      });

      const meta: Record<string, { parentName: string; inheritedValue: any }> =
        {};
      activeTemplates.forEach((template) => {
        const templateId = template.value;
        const inheritedFrom = selectedRow[`inherited_from_${templateId}`];
        const inheritedValue = selectedRow[`inherited_value_${templateId}`];
        meta[templateId] = {
          parentName: inheritedFrom?.name ?? "-",
          inheritedValue,
        };
      });
      setInheritedResetMeta(meta);
      setIsSubjectRootOfTree(
        activeTemplates.every((t) => !selectedRow[`inherited_from_${t.value}`]),
      );
    }
  }, [
    editModalOpen,
    selectedRow,
    selectedAnchorDataTemplates,
    fetchEditDefDetails,
  ]);

  const selectedTemplateIdsCsv = useMemo(
    () =>
      selectedAnchorDataTemplates
        .map((template, idx) => template || lastSelectedTemplates[idx])
        .map((template) => template && template.value)
        .filter(Boolean)
        .join(","),
    [selectedAnchorDataTemplates, lastSelectedTemplates],
  );

  const tableKey = useMemo(
    () =>
      `anchordata-overview-table-${reorderCounter}-${selectedTemplateIdsCsv}`,
    [reorderCounter, selectedTemplateIdsCsv],
  );

  const overviewAdditionalUrlParameter = useMemo(
    () => ({
      template_id__in: selectedTemplateIdsCsv,
      ...(showParentCompaniesOnly ? { parent_id__isnull: true } : {}),
      ...(showEmptyValuesOnly ? { only_show_empty_values: true } : {}),
    }),
    [selectedTemplateIdsCsv, showParentCompaniesOnly, showEmptyValuesOnly],
  );

  const handleResetAllFilters = useCallback(() => {
    setShowParentCompaniesOnly(INITIAL_SHOW_PARENT_COMPANIES_ONLY);
    setShowEmptyValuesOnly(INITIAL_SHOW_EMPTY_VALUES_ONLY);
  }, []);

  const transformOverviewResults = useCallback(
    (results: any[]) => mapOverviewResultsToRows(results),
    [],
  );

  return (
    <Box
      ref={overviewRootRef}
      className={styles.container}
      sx={{
        // rowActionsWrapper is defined in @genericCrudTable.module.css
        "& .MuiTableCell-body[data-index='0'] [class*='rowActionsWrapper']": {
          justifyContent: "center",
        },
      }}
    >
      <AnchorDataOverviewAccordion
        selectedAnchorDataTemplates={selectedAnchorDataTemplates}
        lastSelectedTemplates={lastSelectedTemplates}
        templateSlotIds={templateSlotIds}
        anchorDataTemplateOptions={anchorDataTemplateOptions}
        setSelectedAnchorDataTemplates={setSelectedAnchorDataTemplates}
        setLastSelectedTemplates={setLastSelectedTemplates}
        setTemplateSlotIds={setTemplateSlotIds}
        templateSlotIdCounter={templateSlotIdCounter}
        setReorderCounter={setReorderCounter}
        maxColumns={ANCHOR_DATA_TEMPLATES_COLUMNS_MAX}
        columnKeysWithPendingEdits={columnKeysWithPendingEdits}
        onRemoveLastColumn={handleRemoveLastTemplateColumn}
        disableRemoveLastColumn={isPersistedTemplateSelectionEmpty}
      />

      {selectedAnchorDataTemplates.some(
        (template, idx) => template || lastSelectedTemplates[idx],
      ) && (
          <GenericCrudTable
            key={tableKey}
            entity="anchorData/overview"
            entityAdapter={anchorDataOverviewEntityAdapter}
            columns={columns}
            customPageSize={25}
            maxRowsPerPageOption={25}
            customRowActionColSize={60}
            hideDeleteButtonForRows
            showColumnFilterbyDefault
            disableAddItem
            disableGlobalFilter
            showRowActions={false}
            dropFiltersForRemovedColumns
            refreshTable={refreshTable}
            transformGetResults={transformOverviewResults}
            onSuccessfulGetHandler={(data: any) =>
              setOverviewBaseRows(transformOverviewResults(data?.results ?? []))
            }
            additionalUrlParameter={overviewAdditionalUrlParameter}
            hasAdditionalFiltersToReset={
              showParentCompaniesOnly !==
                INITIAL_SHOW_PARENT_COMPANIES_ONLY ||
              showEmptyValuesOnly !== INITIAL_SHOW_EMPTY_VALUES_ONLY
            }
            onResetAllFilters={handleResetAllFilters}
            editDisplayModeOverride={inlineEditMode ? "cell" : undefined}
            tableInstanceRef={tableInstanceRef}
            bottomToolbarPaginationAlign={inlineEditMode ? "center" : undefined}
            additionalCustomTopToolbarElements={() => inlineEditToolbarElements}
            displayDataTransform={displayDataTransformMemo}
          />
        )}

      <AnchorDataOverviewModal
        open={editModalOpen}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        setEditModalOpen={setEditModalOpen}
        selectedAnchorDataTemplates={selectedAnchorDataTemplates}
        anchorDataTemplateTypes={anchorDataTemplateTypes}
        anchorDataTemplateDetails={anchorDataTemplateDetails}
        anchorDataValues={anchorDataValues}
        setAnchorDataValues={setAnchorDataValues}
        valueMaps={valueMaps}
        setValueMaps={setValueMaps}
        typeMappings={typeMappings}
        setTypeMappings={setTypeMappings}
        objectListFields={objectListFields}
        setObjectListFields={setObjectListFields}
        fieldErrors={fieldErrors}
        setFieldErrors={setFieldErrors}
        resetToInheritedTemplates={resetToInheritedTemplates}
        setResetToInheritedTemplates={setResetToInheritedTemplates}
        inheritedResetMeta={inheritedResetMeta}
        setInheritedResetMeta={setInheritedResetMeta}
        isSubjectRootOfTree={isSubjectRootOfTree}
        setIsSubjectRootOfTree={setIsSubjectRootOfTree}
        setRefreshTable={setRefreshTable}
        inputRefs={inputRefs}
        fetchSubjects={fetchSubjects}
        kaerCompanyId={kaerCompanyId}
        setError={setError}
        isSameAnchorDataValue={isSameAnchorDataValue}
        formatInheritedForDisplay={formatInheritedForDisplay}
      />

      {inlineEditFloatingButtons}

      {error && (
        <GenericErrorMessageModal
          title={t("error_occurred")}
          error={error}
          onClosehandler={() => setError("")}
        />
      )}

      {saveAllErrorResult && (
        <GenericErrorMessageModal
          title={t("error_occurred")}
          error={[
            `${t("save_all_summary_heading")}:\n${t("save_all_summary_saved", {
              count: saveAllErrorResult.successCount,
            })}, ${t("save_all_summary_failed", {
              count: saveAllErrorResult.failureCount,
            })}`,
            ...saveAllErrorResult.errors,
          ]}
          onClosehandler={() => setSaveAllErrorResult(null)}
        />
      )}
    </Box>
  );
};

export default AnchorDataOverviewList;
